"use client";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useLocale } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import FlightPassengersAndCabinClass from "./FlightPassengersAndCabinClass";
import FlightSearchTabs from "./FlightSearchTabs";
import MultiCityFlights from "./MultiCityFlights";
import AutoComplete from "@/components/flightSearchSection/AutoComplete";
import DatePicker from "@/components/flightSearchSection/DatePicker";
import SeachFormsCampaignListener from "@/components/SeachFormsCampaignListener";
import { useLoading } from "@/contexts/LoadingContext";
import { useModalHelpers } from "@/contexts/ModalContext";
import { useFlightSearch } from "@/hooks/useFlightSearch";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { getPathname, useRouter } from "@/i18n/navigation";
import { dateFormats, formatDate, isBefore } from "@/utils/dateFormatter";
import "react-day-picker/style.css";

const FlightSearchForm = ({
  initialActiveTab,
  initialDepartureCityCode,
  initialReturnCityCode,
  initialDepartureDate,
  initialReturnDate,
  initialPassengers,
  initialCabinClass,
  initialOnlyDirectFlights,
  initialMultiFlights,
  searchButtonTitle,
  scrollToForm = true,
  viewportHeightWrapperActive = false,
  hasDatePickerTabs = true,
} = {}) => {
  // Initial data objesini oluştur
  const initialData = {};
  if (initialActiveTab !== undefined) initialData.activeTab = initialActiveTab;
  if (initialDepartureCityCode)
    initialData.initialDepartureCityCode = initialDepartureCityCode;
  if (initialReturnCityCode)
    initialData.initialReturnCityCode = initialReturnCityCode;
  if (initialDepartureDate) initialData.departureDate = initialDepartureDate;
  if (initialReturnDate) initialData.returnDate = initialReturnDate;
  if (initialPassengers) initialData.passengers = initialPassengers;
  if (initialCabinClass) initialData.cabinClass = initialCabinClass;
  if (initialOnlyDirectFlights !== undefined)
    initialData.onlyDirectFlights = initialOnlyDirectFlights;
  if (initialMultiFlights && initialMultiFlights.length > 0) {
    initialData.initialMultiFlights = initialMultiFlights;

    // Debug: Multi-city initial data
    if (initialActiveTab === 2) {
    }
  }

  const {
    activeTab,
    onlyDirectFlights,
    setActiveTab,
    setOnlyDirectFlights,
    setDepartureDate,
    setReturnDate,
    departureDate,
    returnDate,
    departureCity,
    returnCity,
    toggleDepartureAndReturn,
    passengers,
    cabinClass,
    setPassengersAndCabinClass,
    latestFlightSearches,
    popularCities,
    nearestAirports,
    cheapestFlightsMonthly,
    cheapestFlightsMonthlyByMonth,
    getCheapestFlightsMonthlyByMonth,
    searchForFromCity,
    onSelectDepartureCity,
    onSelectReturnCity,
    generateFlightLink,
    getPassengerInfoText,
    validateForm,
    // Multi-city flights
    multiFlights,
    addMultiFlight,
    removeMultiFlight,
    updateMultiFlight,
    toggleMultiFlightCities,
    oneWayCalendarPrices,
    publicHolidays,
    initialLoading,
  } = useFlightSearch(initialData);

  const t = useTranslationDetector();
  const tForm = useTranslationDetector("flightSearch");
  const locale = useLocale();
  const router = useRouter();
  const { showWithComponent } = useLoading();
  const isMobile = useIsMobile();
  const departureeDatePickerRef = useRef(null);
  const returnDatePickerRef = useRef(null);
  const addReturnButtonRef = useRef(null);
  const [validationErrors, setValidationErrors] = useState({});
  const { errorModal } = useModalHelpers();
  const [passengersErrors, setPassengersErrors] = useState({});
  const [isReturnDateSetByuser, setIsReturnDateSetByuser] = useState(false);

  // Portal state'lerini takip et
  const [isDepartureDatePickerOpen, setIsDepartureDatePickerOpen] =
    useState(false);
  const [isReturnDatePickerOpen, setIsReturnDatePickerOpen] = useState(false);

  // Herhangi bir portal açıksa body scroll'u kilitle
  const shouldLockBodyScroll =
    isMobile && (isDepartureDatePickerOpen || isReturnDatePickerOpen);
  useLockBodyScroll(shouldLockBodyScroll);

  // City seçimi değiştiğinde ve hatalar varsa yeniden validate et
  useEffect(() => {
    // Sadece hatalar mevcutsa yeniden validate et
    const currentErrorCount = Object.keys(validationErrors).length;
    if (currentErrorCount > 0) {
      const newErrors = validateForm();
      const newErrorCount = Object.keys(newErrors).length;

      // Hata sayısı değiştiyse veya hata mesajları değiştiyse güncelle
      if (
        currentErrorCount !== newErrorCount ||
        Object.keys(validationErrors).some(
          (key) => validationErrors[key] !== newErrors[key],
        )
      ) {
        setValidationErrors(newErrors);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departureCity, returnCity, returnDate, validateForm]);

  const onSelectDepartureDate = useCallback(
    (date) => {
      setDepartureDate(date);

      if (!isReturnDateSetByuser) {
        const twoDaysAfter = new Date(date);
        twoDaysAfter.setDate(date.getDate() + 1);
        setReturnDate(twoDaysAfter);
      } else {
        if (isBefore(returnDate, date)) {
          setReturnDate(date);
        }
      }

      // Eğer dönüş sekmesindeyse, return date picker'ı otomatik aç
      // if (activeTab === 1) {
      //   if (!isMobile) {
      //     setTimeout(() => {
      //       returnDatePickerRef.current?.open();
      //     }, 100);
      //   }
      // }
    },
    [setDepartureDate, setReturnDate, activeTab],
  );

  // const onSelectReturnDate = useCallback(
  //   (date, triggerDate) => {
  //     // Edge case: Kullanıcı mevcut seçili bir tarihe tıkladı
  //     // AMA sadece her ikisi de doluysa yeni seçim başlat
  //     if (
  //       departureDate &&
  //       returnDate &&
  //       (isSameDay(triggerDate, departureDate) ||
  //         isSameDay(triggerDate, returnDate))
  //     ) {
  //       setDepartureDate(triggerDate);
  //       setReturnDate(null);
  //       return;
  //     }

  //     // returnDate null ise ve triggerDate departureDate'ten önce ise yeni seçim başlat
  //     if (
  //       departureDate &&
  //       !returnDate &&
  //       isBefore(triggerDate, departureDate)
  //     ) {
  //       setDepartureDate(triggerDate);
  //       setReturnDate(null);
  //       return;
  //     }

  //     // Normal range seçimi devam ediyor
  //     if (!departureDate) {
  //       // İlk seçim
  //       setDepartureDate(triggerDate);
  //       setReturnDate(null);
  //     } else if (!returnDate) {
  //       // İkinci seçim - return date belirleniyor
  //       if (isBefore(triggerDate, departureDate)) {
  //         // Geriye doğru seçim → Departure'ı güncelle
  //         setDepartureDate(triggerDate);
  //         setReturnDate(departureDate);
  //       } else {
  //         // İleriye doğru seçim veya aynı gün → Return'ü set et
  //         setReturnDate(triggerDate);
  //       }
  //       if (!isMobile) {
  //         returnDatePickerRef.current?.close();
  //       }
  //     } else {
  //       // Her iki tarih de dolu → Yeni seçim başlat
  //       setDepartureDate(triggerDate);
  //       setReturnDate(null);
  //     }
  //   },
  //   [setReturnDate, setDepartureDate, departureDate, returnDate, isMobile],
  // );

  const onSelectReturnDate = useCallback(
    (date) => {
      setReturnDate(date);
      if (!isReturnDateSetByuser) {
        setIsReturnDateSetByuser(true);
      }
    },
    [setReturnDate],
  );

  const handleAddReturnClick = useCallback(() => {
    if (!isMobile) {
      const inputRect = addReturnButtonRef.current.getBoundingClientRect();
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const targetPosition = inputRect.top + scrollTop - 200;

      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    }

    setActiveTab(1);
    // Return date picker'ı aç ve state'i güncelle
    setTimeout(() => {
      setIsReturnDatePickerOpen(true);
      returnDatePickerRef.current?.open();
    }, 100);
  }, [setActiveTab]);

  const handleMobileDepartureDateClick = useCallback(() => {
    setActiveTab(0);
    // Return date picker'ı kapat ve state'i güncelle
    setIsReturnDatePickerOpen(false);
    returnDatePickerRef.current?.close();
    // Departure date picker'ı aç ve state'i güncelle
    setTimeout(() => {
      setIsDepartureDatePickerOpen(true);
      departureeDatePickerRef.current?.open();
    }, 100);
  }, [setActiveTab]);

  const handleMobileReturnDateClick = useCallback(() => {
    setActiveTab(1);
    // Departure date picker'ı kapat ve state'i güncelle
    setIsDepartureDatePickerOpen(false);
    departureeDatePickerRef.current?.close();
    // Return date picker'ı aç ve state'i güncelle
    setTimeout(() => {
      setIsReturnDatePickerOpen(true);
      returnDatePickerRef.current?.open();
    }, 150);
  }, [setActiveTab]);

  const validateAndSearch = useCallback(() => {
    const errors = validateForm();
    if (Object.keys(errors).length === 0) {
      if (Object.keys(passengersErrors).length > 0) {
        errorModal({
          title: t("common.invalidPassenger"),
          message: Object.values(passengersErrors)
            .map((error) => error)
            .join(", "),
          size: "sm",
          showCancel: false,
          isBottomSheet: true,
        });
        return;
      }

      // Validasyon başarılı - hatalar varsa temizle
      setValidationErrors({});

      // Son arama bilgilerini localStorage'a kaydet
      const newSearch = {
        departureCity,
        returnCity,
        isOneWay: activeTab === 0,
        departureDateRaw: departureDate,
        departureDate: dateFormats.dayShortMonth(departureDate, locale),
        returnDateRaw: returnDate,
        returnDate:
          activeTab === 1
            ? dateFormats.dayShortMonth(returnDate, locale)
            : null,
      };

      // Mevcut arama geçmişini al
      const existingSearches = JSON.parse(
        localStorage.getItem("latestFlightSearches") || "[]",
      );
      // Aynı rota ve tarihlere sahip kayıtları filtrele
      const toDateStr = (d) =>
        d ? formatDate(d, { format: "YYYY-MM-DD" }) : null;
      const filteredSearches = existingSearches.filter(
        (s) =>
          !(
            s.departureCity?.code === newSearch.departureCity?.code &&
            s.returnCity?.code === newSearch.returnCity?.code &&
            toDateStr(s.departureDateRaw) ===
              toDateStr(newSearch.departureDateRaw) &&
            toDateStr(s.returnDateRaw) === toDateStr(newSearch.returnDateRaw) &&
            s.isOneWay === newSearch.isOneWay
          ),
      );

      // Yeni aramayı array'in başına ekle
      const updatedSearches = [newSearch, ...filteredSearches];

      // localStorage'a kaydet
      localStorage.setItem(
        "latestFlightSearches",
        JSON.stringify(updatedSearches),
      );

      // Loading göster ve yönlendir
      const flightLink = generateFlightLink();
      // event: "custom_event",
      // event_category: "search",
      // event_action: "search_result",
      // search_category: "Flight",
      // search_destination: "SAW-ECN",
      // start_to_end_date: "2025-09-01 , 2025-09-05",
      // search_result_number: 10,

      const localizedPath = getPathname({
        href: flightLink,
        locale,
      });

      window.location.href = localizedPath;
    } else {
      // Validasyon başarısız - hataları göster
      setValidationErrors(errors);
    }
  }, [
    validateForm,
    activeTab,
    departureDate,
    returnDate,
    cabinClass,
    onlyDirectFlights,
    generateFlightLink,
    getPassengerInfoText,
    showWithComponent,
    router,
    locale,
    departureCity,
    returnCity,
    t,
  ]);

  return (
    <div className="flex flex-col gap-4 md:gap-4">
      <FlightSearchTabs activeTab={activeTab} setActiveTab={setActiveTab} />
      <div className={clsx(activeTab !== 2 ? "block" : "hidden")}>
        <div className="shadow-medium flex flex-col gap-0 rounded-xl bg-white md:gap-6 md:rounded-none md:bg-transparent md:shadow-none">
          <div className="flex w-full flex-col md:flex-row md:gap-4">
            <div className="md:shadow-medium flex flex-col items-center rounded-lg bg-white shadow-none md:w-3/4 md:flex-row">
              <div className="flight-search-form-autocomplete-container relative z-[1] flex w-full flex-col items-end md:static md:w-[57%] md:flex-row md:items-center">
                <AutoComplete
                  id="from"
                  label={tForm("from")}
                  searchFunction={searchForFromCity}
                  onSelect={onSelectDepartureCity}
                  isFromDate={true}
                  latestFlightSearches={latestFlightSearches}
                  popularCities={(popularCities ?? []).filter(
                    (city) =>
                      city?.AutoCompleteResult?.code !== returnCity?.code,
                  )}
                  nearestAirports={nearestAirports}
                  value={departureCity?.value}
                  error={validationErrors.departureCity}
                  scrollToForm={scrollToForm}
                  viewportHeightWrapperActive={viewportHeightWrapperActive}
                  placeholder={tForm("searchPlaceholder")}
                />
                <span className="relative z-[11] h-[1px] w-[1px] overflow-visible md:h-6 md:w-6">
                  <button
                    onClick={toggleDepartureAndReturn}
                    className="border-border-weaker flex h-11 w-11 -translate-x-[52px] -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full border-2 bg-white md:-translate-x-[10px] md:-translate-y-[10px]"
                  >
                    <FontAwesomeIcon
                      icon={byPrefixAndName.far["arrow-up-arrow-down"]}
                      className="md:rotate-90"
                    />
                  </button>
                </span>

                <AutoComplete
                  id="to"
                  label={tForm("to")}
                  searchFunction={searchForFromCity}
                  onSelect={onSelectReturnCity}
                  isFromDate={false}
                  latestFlightSearches={latestFlightSearches}
                  popularCities={(popularCities ?? []).filter(
                    (city) =>
                      city?.AutoCompleteResult?.code !== departureCity?.code,
                  )}
                  nearestAirports={nearestAirports}
                  value={returnCity?.value}
                  error={validationErrors.returnCity}
                  scrollToForm={scrollToForm}
                  viewportHeightWrapperActive={viewportHeightWrapperActive}
                  placeholder={tForm("searchPlaceholder")}
                />
              </div>
              <span className="bg-border mx-1 hidden h-[34px] w-[1px] md:block"></span>

              <div className="border-primary-weak date-range-picker-container flex w-full items-center border-b md:w-[43%] md:border-0">
                <div className="min-w-0 flex-1">
                  <DatePicker
                    ref={departureeDatePickerRef}
                    id="flight-departure-date"
                    label={tForm("departureDate")}
                    drawerLabel={tForm("departureDateDrawerLabel")}
                    onSelect={onSelectDepartureDate}
                    value={departureDate}
                    onPortalStateChange={setIsDepartureDatePickerOpen}
                    isFromDate={true}
                    defaultMonth={departureDate}
                    cheapestFlightsMonthly={cheapestFlightsMonthly}
                    cheapestFlightsMonthlyByMonth={
                      cheapestFlightsMonthlyByMonth
                    }
                    getCheapestFlightsMonthlyByMonth={
                      getCheapestFlightsMonthlyByMonth
                    }
                    mobileDepartureClick={handleMobileDepartureDateClick}
                    mobileReturnClick={handleMobileReturnDateClick}
                    departureDate={departureDate}
                    returnDate={returnDate}
                    isRoundTrip={activeTab === 1}
                    oneWayCalendarPrices={oneWayCalendarPrices}
                    publicHolidays={publicHolidays}
                    scrollToForm={scrollToForm}
                    viewportHeightWrapperActive={viewportHeightWrapperActive}
                    hasDatePickerTabs={hasDatePickerTabs}
                  />
                </div>
                <span className="relative flex h-16.5 w-10 shrink-0 items-center justify-center">
                  <span className="bg-primary-weak absolute inset-y-0 left-1/2 w-px -translate-x-1/2"></span>
                  <span className="border-primary-weak relative z-1 flex h-10 w-10 items-center justify-center rounded-full border bg-white">
                    <FontAwesomeIcon
                      icon={byPrefixAndName.fak["calendar-linex"]}
                      className="text-base-line h-6 w-6"
                    />
                  </span>
                </span>
                <div className="-ml-5 min-w-0 flex-1 pr-[6px] pl-[14px]">
                  <button
                    className={clsx(
                      "text-foreground-muted h-full w-full cursor-pointer items-center justify-center gap-1 truncate py-5",
                      activeTab === 0 ? "flex" : "hidden",
                    )}
                    ref={addReturnButtonRef}
                    onClick={handleAddReturnClick}
                  >
                    <span className="flex h-5! w-5! items-center justify-center">
                      <FontAwesomeIcon
                        icon={byPrefixAndName.fak["plusnew"]}
                        className="h-5! w-5!"
                      />
                    </span>
                    <span className="max-w-20.25 truncate font-medium">
                      {tForm("addReturn")}
                    </span>
                  </button>
                  <div
                    className={clsx("relative", activeTab !== 1 && "hidden")}
                  >
                    <DatePicker
                      ref={returnDatePickerRef}
                      id="flight-return-date"
                      label={tForm("returnDate")}
                      drawerLabel={tForm("returnDateDrawerLabel")}
                      onSelect={onSelectReturnDate}
                      value={returnDate}
                      disabled={departureDate}
                      onPortalStateChange={setIsReturnDatePickerOpen}
                      isFromDate={false}
                      defaultMonth={departureDate}
                      cheapestFlightsMonthly={cheapestFlightsMonthly}
                      cheapestFlightsMonthlyByMonth={
                        cheapestFlightsMonthlyByMonth
                      }
                      getCheapestFlightsMonthlyByMonth={
                        getCheapestFlightsMonthlyByMonth
                      }
                      mobileDepartureClick={handleMobileDepartureDateClick}
                      mobileReturnClick={handleMobileReturnDateClick}
                      departureDate={departureDate}
                      returnDate={returnDate}
                      isRoundTrip={activeTab === 1}
                      oneWayCalendarPrices={oneWayCalendarPrices}
                      publicHolidays={publicHolidays}
                      error={validationErrors.returnDate}
                      scrollToForm={scrollToForm}
                      viewportHeightWrapperActive={viewportHeightWrapperActive}
                      hasDatePickerTabs={hasDatePickerTabs}
                    />
                    <button
                      className="absolute top-[14.5px] right-[14.5px] cursor-pointer"
                      onClick={() => setActiveTab(0)}
                    >
                      <FontAwesomeIcon
                        icon={byPrefixAndName.fak["clear-dark"]}
                        className="text-foreground text-[18px]"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <FlightPassengersAndCabinClass
              label={tForm("passengerAndClass")}
              value={{ passengers, cabinClass }}
              onSelect={setPassengersAndCabinClass}
              setErrors={setPassengersErrors}
              scrollToForm={scrollToForm}
              viewportHeightWrapperActive={viewportHeightWrapperActive}
            />
            <div className="p-4 md:p-0">
              <button
                id="flight-search-button"
                type="button"
                onClick={validateAndSearch}
                className="bg-primary hover:bg-primary/90 hover:shadow-soft inline-flex h-[50px] w-full cursor-pointer items-center justify-center gap-2 rounded-lg p-4 text-lg font-semibold text-white transition-all duration-200 ease-in-out md:h-[66px] md:w-max xl:min-w-50"
              >
                <div className="flex items-center gap-2 lg:hidden xl:flex">
                  <span>{searchButtonTitle || t("flight.Search")}</span>
                  <FontAwesomeIcon
                    className="text-[28px]"
                    icon={byPrefixAndName.fak["turna-logo-icon"]}
                  />
                </div>
                <span className="hidden lg:block xl:hidden">
                  <FontAwesomeIcon
                    className="text-[28px]"
                    icon={byPrefixAndName.fas["magnifying-glass"]}
                  />
                </span>
              </button>
            </div>
          </div>

          <span className="only-direct-flights-container px-4 pb-4 md:p-0">
            <label className="inline-flex w-auto cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={onlyDirectFlights}
                onChange={(e) => setOnlyDirectFlights(e.target.checked)}
                className="text-primary border-border-strong checked:border-primary ring-none h-6 w-6 rounded-[6px] border-[1.2px] outline-0 transition-all duration-200 ease-in-out focus:ring-transparent"
              />
              <span>{t("flight.onlyDirectFlights")}</span>
            </label>
          </span>
        </div>
      </div>
      <div className={clsx(activeTab === 2 ? "block" : "hidden")}>
        <MultiCityFlights
          multiFlights={multiFlights}
          addMultiFlight={addMultiFlight}
          removeMultiFlight={removeMultiFlight}
          updateMultiFlight={updateMultiFlight}
          toggleMultiFlightCities={toggleMultiFlightCities}
          searchForFromCity={searchForFromCity}
          latestFlightSearches={latestFlightSearches}
          popularCities={popularCities}
          nearestAirports={nearestAirports}
          passengers={passengers}
          cabinClass={cabinClass}
          setPassengersAndCabinClass={setPassengersAndCabinClass}
          onlyDirectFlights={onlyDirectFlights}
          setOnlyDirectFlights={setOnlyDirectFlights}
          publicHolidays={publicHolidays}
          getPassengerInfoText={getPassengerInfoText}
          defaultMonth={departureDate}
          viewportHeightWrapperActive={viewportHeightWrapperActive}
          initialLoading={initialLoading}
        />
      </div>
      <SeachFormsCampaignListener />
    </div>
  );
};

export default FlightSearchForm;
